"use client";

import React from "react";
import { HeartHandshake, Scale, Landmark, ScrollText, Users, Activity } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const resources = [{
  title: "Personalized Care",
  description: "With a maximum of five residents, our caregivers provide individualized attention and compassionate support tailored to each resident’s needs.",
  icon: Activity,
  link: "/CareServicesPage"
}, {
  title: "Home-Cooked Nutrition",
  description: "Fresh, nutritious meals are prepared daily in our home kitchen and tailored to residents’ dietary needs and preferences.",
  icon: HeartHandshake,
  link: "/CareServicesPage"
}, {
  title: "Professional 24/7 Support",
  description: "Our caregivers provide round-the-clock assistance, ensuring safety, comfort, and peace of mind for residents and their families.",
  icon: Users,
  link: "/AboutUsPage"
}];
const adviceItems = [{
  question: "Boutique Residential Living",
  answer: "Unlike large institutional facilities, Family First Senior Care offers boutique assisted living in Silver Spring, Maryland, with a cozy, familiar atmosphere that reduces stress and promotes emotional well-being. Our small scale allows for a level of intimacy and responsiveness that larger centers simply cannot match."
}, {
  question: "Private & Secure Suites",
  answer: "Each resident enjoys their own private room within our small assisted living home with personalized care, designed for comfort and accessibility within a secure environment. We believe privacy is essential to maintaining dignity and personal identity in senior years."
}, {
  question: "Silver Spring Community",
  answer: "Nestled in a quiet, safe Maryland neighborhood, our residential assisted living in Montgomery County provides the perfect balance of peaceful living and proximity to local medical centers. Our residents enjoy a calm environment while remaining connected to the local Silver Spring community."
}];
export default function Caregiver() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="28736" id="whyfamilieschoosefamilyfirstseniorcare" ref={ref} className="bg-background py-20 relative overflow-hidden text-foreground">
      {}
      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586469/img/parchment_texture_pattern.png')] opacity-5 pointer-events-none mix-blend-multiply" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        {}
        <div className={cn("max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center justify-center p-2 mb-4 rounded-full bg-primary/10 text-primary">
            <HeartHandshake className="w-5 h-5" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-primary mb-6 tracking-tight">
            Why Families Choose Family First Senior Care
          </h2>
          <p className="text-lg text-muted-foreground font-sans max-w-2xl mx-auto leading-relaxed">
            Providing a dignified, warm, and secure boutique home environment for assisted living in Silver Spring, Maryland, where your loved ones are treated with the respect and compassion of family.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {resources.map((resource, i) => <div key={i} data-index={i} className={cn("transition-all duration-700 ease-out delay-[100ms]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
          transitionDelay: `${100 + i * 100}ms`
        }}>
              <Link to={resource.link} className="block h-full group">
                <Card className="h-full border-border/60 bg-card/50 hover:bg-card shadow-sm hover:shadow-xl rounded-3xl transition-all duration-300 group-hover:-translate-y-1">
                  <CardHeader>
                    <div className="mb-4 p-3 w-fit rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      <resource.icon className="w-6 h-6" />
                    </div>
                    <CardTitle className="font-sans text-sm font-bold uppercase tracking-widest text-primary group-hover:text-primary transition-colors">
                      {resource.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed text-muted-foreground">
                      {resource.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </Link>
            </div>)}
        </div>

        {}
        <div className={cn("max-w-3xl mx-auto transition-all duration-700 ease-out delay-500", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="text-center mb-10">
            <ScrollText className="w-8 h-8 text-secondary mx-auto mb-4 opacity-80" />
            <h3 className="text-2xl md:text-3xl font-serif italic text-primary">The Family First Difference</h3>
            <p className="text-muted-foreground mt-2 font-sans">
              Boutique care focused on quality of life
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full bg-card/30 rounded-3xl border border-border/40 px-6 py-2 shadow-sm">
            {adviceItems.map((item, i) => <AccordionItem key={i} value={`item-${i}`} className="border-b-border/40 last:border-0" data-index={i}>
                <AccordionTrigger className="text-lg font-serif text-primary hover:text-secondary transition-colors py-5 text-left">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6 font-sans">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

      </div>
    </section>;
}